import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  Check,
  X,
  Star,
  Plus,
  Minus,
  Sparkles,
  Compass,
  ShieldCheck,
  Menu,
  Play,
  User,
} from "lucide-react";
import Admin from "./Admin";
import Danke from "./Danke";
import { Impressum, Datenschutz } from "./Legal";
import Gold from "./Gold";
import IdCertify from "./IdCertify";
import Backoffice from "./Backoffice";
import Finanzcheck from "./Finanzcheck";
import Partner from "./Partner";
import { AuthProvider, useAuth } from "./AuthContext";
import { PrivateRoute } from "./PrivateRoute";
import { Login, Register, PendingApproval } from "./AuthPages";

const TALLY_URL = "/finanzcheck";
const YOUTUBE_ID = "t3zATHeMBjs";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

/* ─────────────────────────── NAV ─────────────────────────── */
const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const dashboardHref = user
    ? (user.role === "admin" ? "/backoffice" : "/partner")
    : null;
  const dashboardLabel = user?.role === "admin" ? "Backoffice" : "Mein Bereich";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ivory-100/80 backdrop-blur-xl border-b border-ink-700/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <a href="#top" data-testid="logo-link" className="group flex items-center gap-3">
          <img
            src="/moonshine-logo.png"
            alt="Moonshine Solutions"
            className="h-14 w-14 rounded-full object-cover shadow-sm ring-1 ring-gold-500/30"
          />
          <div className="leading-tight">
            <div className="font-display font-medium text-ink-900 text-[15px] tracking-tight">
              Moonshine
            </div>
            <div className="font-sans text-[11px] uppercase tracking-[0.18em] text-ink-500">
              Solutions
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10 text-[14px] text-ink-700">
          <a href="/gold" className="link-underline" data-testid="nav-zinseszins">Zinseszinsrechner</a>
          <a href="#faq" className="link-underline" data-testid="nav-faq">FAQ</a>
        </nav>

        <div className="flex items-center gap-3">
          {dashboardHref && (
            <a
              href={dashboardHref}
              data-testid="nav-dashboard-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-ink-700 hover:text-ink-900 font-medium transition"
            >
              <User className="w-4 h-4" /> {dashboardLabel}
            </a>
          )}
          {!user && (
            <a
              href="/login"
              data-testid="nav-login-link"
              className="hidden sm:inline-flex items-center text-sm text-ink-700 hover:text-ink-900 font-medium transition"
            >
              Login
            </a>
          )}
          <a
            href={TALLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 hover:text-gold-300 px-5 py-2.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-[0_4px_14px_-2px_rgba(15,15,15,0.4)] ring-1 ring-gold-500/30"
          >
            Finanzcheck starten
            <ArrowUpRight className="w-4 h-4" />
          </a>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className="md:hidden w-10 h-10 rounded-full bg-ink-900 text-gold-300 flex items-center justify-center"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-ivory-100 border-t border-ink-700/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4 text-ink-700">
              <a href="/gold" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-zinseszins">Zinseszinsrechner</a>
              <a href="#faq" onClick={() => setMobileOpen(false)}>FAQ</a>
              {dashboardHref && (
                <a href={dashboardHref} onClick={() => setMobileOpen(false)}
                  data-testid="mobile-dashboard-link"
                  className="text-gold-700 font-medium flex items-center gap-2">
                  <User className="w-4 h-4" /> {dashboardLabel}
                </a>
              )}
              {!user && (
                <a href="/login" onClick={() => setMobileOpen(false)}
                  data-testid="mobile-login-link"
                  className="text-ink-700 font-medium">Login</a>
              )}
              <a
                href={TALLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-cta-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-ink-900 text-gold-300 px-5 py-3 text-sm font-medium"
              >
                Finanzcheck starten <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

/* ─────────────────────────── HERO ─────────────────────────── */
const Hero = () => {
  return (
    <section id="top" className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 overflow-hidden">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      {/* decorative ring */}
      <div className="absolute -right-40 top-20 w-[700px] h-[700px] rounded-full border border-gold-500/15 pointer-events-none" />
      <div className="absolute -right-40 top-20 w-[500px] h-[500px] rounded-full border border-ink-700/10 translate-x-20 translate-y-20 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full bg-ivory-200/60 border border-gold-500/30 backdrop-blur-sm">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold-500 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-gold-500" />
            </span>
            <span className="text-[12px] uppercase tracking-[0.22em] font-semibold text-gold-700">
              Gut, dass du hier bist.
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="font-display font-medium text-ink-900 text-[44px] sm:text-6xl lg:text-[5.2rem] leading-[0.98] tracking-[-0.035em]"
          >
            Erkenne, wo du{" "}
            <span className="italic font-serif-display font-normal text-gold-gradient">
              finanziell
            </span>{" "}
            wirklich stehst.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-8 max-w-2xl mx-auto text-lg lg:text-xl text-ink-500 leading-relaxed"
          >
            In wenigen Minuten zum klaren Überblick über deine Situation – und endlich
            wissen, welche Stellschrauben du drehen kannst. Ohne Verkaufsdruck.
            Ohne Fachchinesisch.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={TALLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 px-7 py-5 text-base font-medium transition-all duration-300 hover:-translate-y-1 shadow-[0_10px_30px_-10px_rgba(15,15,15,0.5)] ring-1 ring-gold-500/30"
            >
              Kostenlosen Vorsorgecheck starten
              <span className="w-7 h-7 rounded-full bg-gold-500 flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                <ArrowUpRight className="w-4 h-4 text-ink-900" strokeWidth={2.5} />
              </span>
            </a>
            <a
              href="/gold"
              data-testid="hero-secondary-link"
              className="inline-flex items-center gap-2 text-ink-900 font-medium hover:text-gold-700 transition-colors"
            >
              <span className="link-underline">Zinseszins-Potenzial berechnen</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-12 flex flex-wrap items-center justify-center gap-6 lg:gap-10">
            <div className="flex items-center gap-2.5">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-gold-500 text-gold-500" />
                ))}
              </div>
              <span className="text-sm text-ink-500">ProvenExpert geprüft</span>
            </div>
            <div className="h-8 w-px bg-ink-700/20 hidden sm:block" />
            <div className="text-sm">
              <span className="font-display text-ink-900 text-lg font-medium">500+</span>{" "}
              <span className="text-ink-500">Menschen begleitet</span>
            </div>
            <div className="h-8 w-px bg-ink-700/20 hidden sm:block" />
            <div className="text-sm">
              <span className="font-display text-ink-900 text-lg font-medium">100 %</span>{" "}
              <span className="text-ink-500">unverbindlich</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────── VIDEO SECTION ─────────────────────────── */
const VideoSection = () => {
  return (
    <section id="video" className="relative py-12 lg:py-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          {/* Gold corner accents */}
          <div className="absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2 border-gold-500 rounded-tl-lg" />
          <div className="absolute -top-3 -right-3 w-10 h-10 border-t-2 border-r-2 border-gold-500 rounded-tr-lg" />
          <div className="absolute -bottom-3 -left-3 w-10 h-10 border-b-2 border-l-2 border-gold-500 rounded-bl-lg" />
          <div className="absolute -bottom-3 -right-3 w-10 h-10 border-b-2 border-r-2 border-gold-500 rounded-br-lg" />

          <div className="video-frame rounded-2xl overflow-hidden bg-ink-900 aspect-video">
            <iframe
              data-testid="hero-video"
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${YOUTUBE_ID}?rel=0&modestbranding=1`}
              title="Moonshine Solutions – Finanzcheck"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-ink-500">
            <div className="flex items-center gap-2">
              <Play className="w-4 h-4 text-gold-600" fill="currentColor" />
              <span>Lerne in wenigen Minuten, worum es geht.</span>
            </div>
            <a
              href={TALLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="video-cta-link"
              className="inline-flex items-center gap-2 text-ink-900 font-medium link-underline"
            >
              Direkt zum Check <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>

      {/* Marquee */}
      <div className="relative mt-20 lg:mt-28 overflow-hidden border-y border-ink-700/10 py-6 bg-ivory-200/30">
        <div className="marquee-wrap text-ink-900/25 font-display text-2xl lg:text-4xl whitespace-nowrap">
          {Array(2).fill(null).flatMap((_, k) =>
            ["Klarheit", "✦", "Vertraulich", "✦", "Ohne Verkaufsdruck", "✦", "Plan statt Hoffnung", "✦", "Schritt für Schritt", "✦"].map((w, i) => (
              <span key={`${k}-${i}`} className="px-4">{w}</span>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────── NEGATIVE vs POSITIVE ─────────────────────────── */
const NOT_LIST = [
  "Nein, hier wird dir keine weitere Säule 3a verkauft.",
  "Nein, du musst nichts unterschreiben – nur verstehen, wo du stehst.",
  "Nein, ich rede nicht über Produkte – sondern erstmal über dich.",
  "Nein, du brauchst keinen Finanz-Wortschatz für Fortgeschrittene.",
  "Nein, du bekommst keinen Verkaufsdruck – sondern Klarheit.",
];
const YES_LIST = [
  "Eine sofortige Potentialanalyse anhand deiner Angaben.",
  "Einen klaren Blick auf deine Möglichkeiten.",
  "Eine Beratung, die zu deinem Leben passt.",
  "Entscheidungen, die du wirklich verstehst.",
];

const NotGetVsGet = () => {
  return (
    <section id="bekommst" className="py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="max-w-3xl mb-16 lg:mb-20"
        >
          <motion.div variants={fadeUp} className="text-xs uppercase tracking-[0.22em] font-bold text-gold-700 mb-4">
            Ehrlich · Direkt · Auf Augenhöhe
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-medium text-ink-900 text-4xl lg:text-6xl tracking-tight leading-[1.05]">
            Was du{" "}
            <span className="italic font-serif-display text-gold-gradient">hier</span>{" "}
            bekommst – und was nicht.
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Negative card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 rounded-3xl border border-ink-700/10 bg-ivory-50 p-8 lg:p-12 relative overflow-hidden"
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-ivory-200/70" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-ivory-200 border border-ink-700/10 mb-8">
                <X className="w-3.5 h-3.5 text-ink-700" />
                <span className="text-xs uppercase tracking-widest font-semibold text-ink-700">Was du nicht bekommst</span>
              </div>
              <ul className="space-y-5">
                {NOT_LIST.map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + i * 0.06 }}
                    className="flex gap-4 items-start"
                  >
                    <span className="flex-shrink-0 mt-1 w-6 h-6 rounded-full border-2 border-ink-900 flex items-center justify-center">
                      <X className="w-3.5 h-3.5 text-ink-900" strokeWidth={2.5} />
                    </span>
                    <span className="text-ink-700 leading-relaxed">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Positive card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 rounded-3xl bg-ink-900 p-8 lg:p-12 relative overflow-hidden text-ivory-100 ring-1 ring-gold-500/20"
          >
            <div className="absolute inset-0 opacity-20"
              style={{ background: "radial-gradient(circle at 80% 20%, rgba(201,169,97,0.55), transparent 50%)" }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/30 backdrop-blur mb-8">
                <Check className="w-3.5 h-3.5 text-gold-300" strokeWidth={3} />
                <span className="text-xs uppercase tracking-widest font-semibold text-gold-300">Dafür bekommst du</span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {YES_LIST.map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.15 + i * 0.08 }}
                    className="flex flex-col gap-3"
                  >
                    <div className="w-10 h-10 rounded-2xl bg-gold-500 flex items-center justify-center">
                      <Check className="w-5 h-5 text-ink-900" strokeWidth={3} />
                    </div>
                    <span className="font-display text-lg leading-snug text-ivory-100/95">
                      {item}
                    </span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-10 pt-8 border-t border-gold-500/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-ivory-100/70 text-sm max-w-xs">
                  Kostenlos. Vertraulich. In deinem Tempo.
                </p>
                <a
                  href={TALLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="benefits-cta-button"
                  className="inline-flex items-center gap-2 text-gold-300 font-medium link-underline"
                >
                  Jetzt starten <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────── EMPATHY STORY ─────────────────────────── */
const Empathy = () => {
  const questions = [
    "Habe ich überhaupt genug vorgesorgt?",
    "Was passiert, wenn ich nicht mehr arbeiten kann?",
    "Reicht meine Rente eigentlich?",
  ];

  return (
    <section id="story" className="py-24 lg:py-32 bg-ivory-200/40 relative grain-overlay">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="text-xs uppercase tracking-[0.22em] font-bold text-gold-700 mb-6">
            Eine kurze Wahrheit
          </motion.div>

          <motion.h2
            variants={fadeUp}
            className="font-display font-medium text-ink-900 text-4xl lg:text-6xl tracking-tight leading-[1.05] mb-12"
          >
            Es ist <span className="italic font-serif-display text-gold-gradient">nicht</span> deine Schuld.
          </motion.h2>

          <motion.div variants={fadeUp} className="prose-lg space-y-6 text-ink-700/90 text-lg leading-relaxed">
            <p>
              Nicht zu wissen, ob du gut genug für später vorgesorgt hast. Ob deine
              Rente reicht. Ob du im Ernstfall abgesichert wärst. <em>Woher solltest du
              das auch wissen?</em>
            </p>
            <p>
              Du hast gearbeitet. Jeden Tag. Du hast deine Familie versorgt, deine
              Rechnungen bezahlt. Du hast das gemacht, was man dir gesagt hat:
              arbeiten, sparen, durchhalten.
            </p>
            <p className="text-ink-900 font-display text-2xl lg:text-3xl leading-[1.3] tracking-tight !mt-12">
              Aber keiner hat dir je wirklich erklärt, wie du herausfindest, wo du
              gerade stehst.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="mt-14 space-y-3">
            <div className="text-sm text-ink-500 mb-3 uppercase tracking-widest">Jetzt stellst du dir Fragen wie …</div>
            {questions.map((q, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.1 }}
                className="border-l-2 border-gold-500 pl-5 py-2"
              >
                <span className="font-serif-display italic text-xl lg:text-2xl text-ink-800">
                  „{q}"
                </span>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={fadeUp} className="mt-14 p-8 lg:p-10 rounded-3xl bg-ivory-50 border border-ink-700/10">
            <p className="font-display text-xl lg:text-2xl text-ink-900 leading-snug">
              Du bist nicht allein. Tausende vor dir haben sich genau das gefragt –
              und meistens einfach gehofft, dass es „schon irgendwie reichen wird".
            </p>
            <p className="mt-4 text-ink-700">
              Aber Hoffnung ist kein Plan. Die gute Nachricht: Du kannst heute den
              ersten Schritt machen – und endlich Klarheit schaffen.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────── TWO OPTIONS ─────────────────────────── */
const TwoOptions = () => {
  return (
    <section className="py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="max-w-3xl mb-16"
        >
          <motion.div variants={fadeUp} className="text-xs uppercase tracking-[0.22em] font-bold text-gold-700 mb-4">
            Die Entscheidung liegt bei dir
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-medium text-ink-900 text-4xl lg:text-6xl tracking-tight leading-[1.05]">
            Beende deine Vorsorgeängste{" "}
            <span className="italic font-serif-display text-gold-gradient">ein für alle Mal.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-6 text-lg text-ink-500">
            Du hast zwei Möglichkeiten – wähle den Weg, der zu deinem Leben passt.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Option 1 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl border border-ink-700/10 bg-ivory-50 p-8 lg:p-12 relative"
          >
            <div className="flex items-center justify-between mb-8">
              <span className="text-xs uppercase tracking-[0.22em] text-ink-500 font-bold">Option 01</span>
              <span className="text-ink-500/40 font-display text-5xl">A</span>
            </div>
            <h3 className="font-display text-2xl lg:text-3xl text-ink-700 leading-tight mb-6">
              Du machst weiter wie bisher.
            </h3>
            <p className="text-ink-700/80 leading-relaxed">
              Unklare finanzielle Situation. Unsicherheit bei der Vorsorge. Das
              Gefühl, nie wirklich voranzukommen. Komplexe Infos, widersprüchliche
              Empfehlungen – und das ständige „Eigentlich könnte ich mehr aus meinem
              Geld machen, aber wie?"
            </p>
            <div className="mt-10 flex items-center gap-2 text-ink-500 text-sm">
              <Compass className="w-4 h-4" /> Ergebnis: Hoffnung statt Plan.
            </div>
          </motion.div>

          {/* Option 2 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="rounded-3xl bg-gradient-to-br from-ink-800 to-ink-900 p-8 lg:p-12 relative overflow-hidden text-ivory-100 ring-1 ring-gold-500/25"
          >
            <div className="absolute -bottom-10 -right-10 w-60 h-60 rounded-full bg-gold-500/15 blur-3xl" />
            <div className="relative">
              <div className="flex items-center justify-between mb-8">
                <span className="text-xs uppercase tracking-[0.22em] font-bold text-gold-300">Option 02 · Empfohlen</span>
                <span className="font-display text-5xl text-gold-500/50">B</span>
              </div>
              <h3 className="font-display text-2xl lg:text-3xl leading-tight mb-6">
                Du nimmst dir 3 Minuten – und schaffst Klarheit.
              </h3>
              <p className="text-ivory-100/85 leading-relaxed">
                Du bekommst Klarheit über deine aktuelle Lage, erkennst echte
                Optimierungs-Potenziale und entwickelst gemeinsam mit mir einen
                klaren, umsetzbaren Plan. Ohne Druck. Ohne Fachchinesisch. Dafür
                individuell, ehrlich und mit Weitblick.
              </p>

              <a
                href={TALLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="options-cta-button"
                className="mt-10 inline-flex items-center gap-3 rounded-full bg-gold-500 hover:bg-gold-400 text-ink-900 px-6 py-4 text-base font-medium transition-all duration-300 hover:-translate-y-0.5"
              >
                Jetzt Vorsorgecheck starten
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────── FAQ ─────────────────────────── */
const FAQS = [
  {
    q: "Für wen ist der Check geeignet?",
    a: "Der Check ist ideal für Menschen, die ihr Vermögen sicher, inflationsgeschützt und steueroptimiert aufbauen möchten – ganz ohne komplizierte Finanzsprache, dafür mit einem klaren Plan, individueller Begleitung und echtem Fokus auf ihre Lebensziele. Oder wenn du ganz einfach Klarheit willst, wie du aktuell finanziell dastehst.",
  },
  {
    q: "Was, wenn ich nicht genug Erfahrung mit Finanzen habe?",
    a: "Kein Problem! Das System ist genau darauf ausgelegt, dir Schritt für Schritt zu helfen – ganz gleich, wie viel du bisher über Finanzen weisst. Wir beginnen mit den Grundlagen und entwickeln gemeinsam einen Plan, der zu dir und deiner Situation passt. Du musst keine Finanzexpertin oder kein Finanzexperte sein, um erfolgreich zu sein – ich begleite dich auf jedem Schritt des Weges.",
  },
  {
    q: "Kann ich auch mit wenig Kapital starten?",
    a: "Absolut! Es geht nicht immer um hohe Beträge. Auch mit kleineren Summen lässt sich ein solider Vermögensaufbau aufbauen. Wir finden einen Weg, der zu deinem Budget passt und dich Schritt für Schritt weiterbringt. Der wichtigste Faktor ist, frühzeitig zu starten und einen nachhaltigen Plan zu verfolgen.",
  },
  {
    q: "Wie soll ich mich um meine Finanzen kümmern, wenn ich wenig Zeit habe?",
    a: "Ich verstehe, dass der Alltag hektisch ist. Genau deshalb biete ich dir eine Lösung, die dir keine zusätzliche Zeit raubt. Ich übernehme die Planung und erkläre dir alles in verständlichen Schritten. Du kannst dich darauf verlassen, dass der Prozess effizient und stressfrei bleibt – du musst dich nicht in Finanzthemen vertiefen.",
  },
  {
    q: "Was, wenn ich schon etwas älter bin?",
    a: "Egal, in welchem Lebensabschnitt du dich befindest, es ist nie zu spät, um deine finanzielle Zukunft aktiv zu gestalten. Auch für ältere Menschen gibt es massgeschneiderte Lösungen, um Vermögen aufzubauen, für den Ruhestand vorzusorgen und steuerliche Vorteile zu nutzen. Wir passen die Strategie ganz an deine Bedürfnisse und deine Lebenssituation an.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 lg:py-32 relative">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16"
        >
          <div className="lg:col-span-5">
            <motion.div variants={fadeUp} className="text-xs uppercase tracking-[0.22em] font-bold text-gold-700 mb-4">
              Häufige Fragen
            </motion.div>
            <motion.h2 variants={fadeUp} className="font-display font-medium text-ink-900 text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Alles, was du wissen{" "}
              <span className="italic font-serif-display text-gold-gradient">möchtest.</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="mt-6 text-ink-500">
              Noch eine Frage offen? Starte einfach den Check – wir gehen alles
              persönlich mit dir durch.
            </motion.p>
          </div>

          <div className="lg:col-span-7">
            {FAQS.map((item, i) => {
              const isOpen = open === i;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="border-b border-ink-700/15"
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                  >
                    <span className="font-display text-lg lg:text-xl text-ink-900 group-hover:text-gold-700 transition-colors">
                      {item.q}
                    </span>
                    <span className={`flex-shrink-0 w-9 h-9 rounded-full border border-ink-700/20 flex items-center justify-center transition-all ${isOpen ? "bg-ink-900 text-gold-300 border-ink-900 rotate-180" : "text-ink-700"}`}>
                      {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 pr-12 text-ink-700/85 leading-relaxed">
                          {item.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────── FINAL CTA ─────────────────────────── */
const FinalCTA = () => {
  return (
    <section className="py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative rounded-[40px] bg-gradient-to-br from-ink-800 via-ink-900 to-ink-900 px-8 py-16 lg:px-20 lg:py-24 overflow-hidden text-ivory-100 ring-1 ring-gold-500/25"
        >
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gold-500/20 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-gold-500/10 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-gold-300 mb-6">
                Vertraulich · Klar · Ohne Verpflichtung
              </div>
              <h2 className="font-display font-medium text-4xl lg:text-6xl tracking-tight leading-[1.05]">
                Mehr aus deinen Finanzen –{" "}
                <span className="italic font-serif-display text-gold-gradient">ohne alles</span>{" "}
                umzustrukturieren.
              </h2>
              <p className="mt-6 text-lg text-ivory-100/75 max-w-2xl">
                Fülle deinen kostenlosen Finanz- und Vorsorgecheck aus. In wenigen
                Minuten erhältst du eine persönliche Einschätzung – und einen klaren
                Weg nach vorn.
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col items-start lg:items-end gap-4">
              <a
                href={TALLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-cta-button"
                className="group inline-flex items-center gap-3 rounded-full bg-gold-500 hover:bg-gold-400 text-ink-900 px-8 py-5 text-lg font-medium transition-all duration-300 hover:-translate-y-1 shadow-2xl"
              >
                Jetzt Check starten
                <span className="w-8 h-8 rounded-full bg-ink-900 text-gold-300 flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                </span>
              </a>
              <div className="text-sm text-ivory-100/60">3 Minuten · 0.– CHF</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────── FINANZCHECK REPEAT CTA ─────────────────────────── */
const FinanzcheckRepeatCTA = () => (
  <section className="pb-24 lg:pb-32">
    <div className="max-w-5xl mx-auto px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
        data-testid="finanzcheck-repeat-cta"
        className="rounded-[36px] bg-ivory-200/70 border border-gold-500/25 px-8 py-12 lg:px-16 lg:py-16 text-center"
      >
        <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-gold-700 mb-4">
          Deine persönliche Erstanalyse · Ergebnis in wenigen Minuten
        </div>
        <h3 className="font-display text-3xl lg:text-4xl font-medium tracking-tight leading-[1.1] max-w-3xl mx-auto">
          Starte jetzt deinen{" "}
          <span className="italic font-serif-display text-gold-gradient">Vorsorge- & Finanzcheck</span>.
        </h3>
        <p className="mt-5 text-base lg:text-lg text-ink-700 max-w-2xl mx-auto">
          14 kurze Fragen. Sofort eine persönliche Erstanalyse per E-Mail mit deiner
          CHF-Potenzialrechnung, deiner möglichen Rente ab 65 und den nächsten Schritten.
        </p>
        <a
          href="/finanzcheck"
          data-testid="finanzcheck-repeat-cta-button"
          className="mt-9 inline-flex items-center gap-3 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 px-8 py-5 text-base font-medium transition-all duration-300 hover:-translate-y-1 shadow-2xl ring-1 ring-gold-500/30"
        >
          Finanzcheck starten
          <ArrowUpRight className="w-4 h-4" />
        </a>
        <div className="mt-4 text-xs text-ink-500">3 Minuten · 0.– CHF · vertraulich</div>
      </motion.div>
    </div>
  </section>
);

/* ─────────────────────────── FOOTER ─────────────────────────── */
const Footer = () => {
  return (
    <footer className="bg-ivory-200/50 border-t border-ink-700/10 py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/moonshine-logo.png"
                alt="Moonshine Solutions"
                className="h-14 w-14 rounded-full object-cover ring-1 ring-gold-500/30"
              />
              <div className="leading-tight">
                <div className="font-display font-medium text-ink-900 text-[15px]">Moonshine</div>
                <div className="font-sans text-[11px] uppercase tracking-[0.18em] text-ink-500">Solutions</div>
              </div>
            </div>
            <p className="text-ink-700/80 text-sm leading-relaxed max-w-sm">
              Wir helfen dir, Finanzen und Geld besser zu verstehen – kostenlos.
              Klarheit statt Verkaufsdruck. Plan statt Hoffnung.
            </p>
            <a
              href="https://moonshine-solutions.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-main-site-link"
              className="mt-4 inline-flex items-center gap-1.5 text-xs text-ink-500 hover:text-gold-700 transition-colors"
            >
              Mehr über uns &amp; weitere Angebote <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-widest text-ink-500 mb-4">Navigation</div>
            <ul className="space-y-2 text-ink-700">
              <li><a href="/gold" className="hover:text-gold-700 transition-colors">Zinseszinsrechner</a></li>
              <li><a href="#faq" className="hover:text-gold-700 transition-colors">FAQ</a></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-widest text-ink-500 mb-4">Loslegen</div>
            <a
              href={TALLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 px-5 py-3 text-sm font-medium transition-all ring-1 ring-gold-500/30"
            >
              Finanzcheck starten <ArrowUpRight className="w-4 h-4" />
            </a>
            <p className="mt-4 text-xs text-ink-500">
              Vertraulich. Ohne Verkaufsdruck. Kostenlos.
            </p>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-ink-700/10 flex flex-col md:flex-row justify-between gap-3 text-xs text-ink-500">
          <div>© {new Date().getFullYear()} Moonshine Solutions GmbH · Venusweg 42, 3185 Schmitten FR</div>
          <nav aria-label="Rechtliches" className="flex gap-6">
            <a href="/impressum" data-testid="footer-impressum-link" className="hover:text-ink-900">Impressum</a>
            <a href="/datenschutz" data-testid="footer-datenschutz-link" className="hover:text-ink-900">Datenschutz</a>
          </nav>
        </div>
      </div>
    </footer>
  );
};

/* ─────────────────────────── APP ─────────────────────────── */
const Landing = () => (
  <div className="App font-sans antialiased bg-ivory-100 text-ink-900">
    <Nav />
    <main>
      <Hero />
      <VideoSection />
      <NotGetVsGet />
      <Empathy />
      <TwoOptions />
      <FAQ />
      <FinalCTA />
      <FinanzcheckRepeatCTA />
    </main>
    <Footer />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/finanzcheck" element={<Finanzcheck />} />
          <Route path="/Finanzcheck" element={<Finanzcheck />} />
          <Route path="/finanzcheck/:gp" element={<Finanzcheck />} />
          <Route path="/Finanzcheck/:gp" element={<Finanzcheck />} />
          <Route path="/danke" element={<Danke />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pending" element={<PendingApproval />} />
          <Route path="/Gold" element={<Gold />} />
          <Route path="/gold" element={<Gold />} />
          <Route path="/ID" element={<PrivateRoute><IdCertify /></PrivateRoute>} />
          <Route path="/id" element={<PrivateRoute><IdCertify /></PrivateRoute>} />
          <Route path="/backoffice" element={<PrivateRoute adminOnly><Backoffice /></PrivateRoute>} />
          <Route path="/partner" element={<PrivateRoute><Partner /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute adminOnly><Admin /></PrivateRoute>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
